import FilterListIcon from '@mui/icons-material/FilterList'
import { Popper, IconButton, ClickAwayListener, Box, Fade } from '@mui/material'
import { STATUS_CODE } from 'config'
import { useState, useRef, useEffect, useCallback } from 'react'
import { useSearchParams, useLocation } from 'react-router-dom'
import { dateFormat } from 'utils/format'

import DocNameSearch from './DocNameSearch'
import Filters from './Filters'

export interface SearchParams {
  name: string
  docTypeName: string
  status: number | ''
  updateDateFrom: Date | null
  updateDateTo: Date | null
  updateUserName: string
  assigneeName: string
  subGroup: string
  verifyStatus: number
}

const INIT_SEARCH_PARAMS: SearchParams = {
  name: '',
  docTypeName: '',
  status: -1,
  updateDateFrom: null,
  updateDateTo: null,
  updateUserName: '',
  assigneeName: '',
  subGroup: '',
  verifyStatus: -1,
}

function clickInside(clickEvent, element) {
  const { clientX, clientY } = clickEvent
  if (!(element && clientX && clientY)) {
    return false
  }

  const { x, y, width, height } = element.getBoundingClientRect()
  return x + width >= clientX && clientX >= x && y + height >= clientY && clientY >= y
}

const SearchBar = () => {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const searchEntries = Object.fromEntries(searchParams)
  const initialSearch: SearchParams = {
    ...INIT_SEARCH_PARAMS,
    ...searchEntries,
  }
  if (searchEntries['status']) {
    initialSearch.status = parseInt(searchEntries['updateDateFrom'])
  }
  if (searchEntries['updateDateFrom']) {
    initialSearch['updateDateFrom'] = new Date(searchEntries['updateDateFrom'])
  }
  if (searchEntries['updateDateTo']) {
    initialSearch['updateDateTo'] = new Date(searchEntries['updateDateTo'])
  }
  if (searchEntries['verifyStatus']) {
    initialSearch.verifyStatus = parseInt(searchEntries['verifyStatus'])
  }
  const [search, setSearch] = useState(initialSearch)

  const [openPopper, setOpenPopper] = useState(false)
  const [usingCustomDate, setUsingCustomDate] = useState(false)

  const popperAnchorRef = useRef(null)
  const filterPopperRef = useRef(null)

  const handelResetClick = () => {
    setUsingCustomDate(false)
    setSearch(() => INIT_SEARCH_PARAMS)
  }

  const onChange = useCallback(({ target }) => {
    const { value, name } = target
    setSearch((s) => {
      const ns = { ...s }
      ns[name] = value
      return ns
    })
  }, [])
  const handleClickAway = (event) => {
    // work around Mui-Select and ClickAway bug
    if (!clickInside(event, filterPopperRef.current)) {
      setOpenPopper(false)
    }
  }
  const onSubmit = (e) => {
    e.preventDefault()
    setSearchParams((s) => {
      const rtn: Record<string, any> = { ...search }
      rtn.updateDateFrom =
        search.updateDateFrom && !isNaN(search.updateDateFrom.getTime())
          ? dateFormat(search.updateDateFrom)
          : ''
      rtn.updateDateTo =
        search.updateDateTo && !isNaN(search.updateDateTo.getTime())
          ? dateFormat(search.updateDateTo)
          : ''
      rtn.status = search.status !== -1 ? search.status : ''
      rtn.verifyStatus = search.verifyStatus !== -1 ? search.verifyStatus : ''
      const perPage = s.get('perPage')
      if (perPage) rtn.perPage = perPage
      return rtn
    })
    setOpenPopper(false)
  }

  const isDeleted = location.pathname.endsWith('/deleted')
  useEffect(() => {
    if (search.status === -1) return
    if (isDeleted) {
      if (search.status !== STATUS_CODE['削除']) {
        onChange({ target: { name: 'status', value: -1 } })
      }
    } else {
      if (search.status === STATUS_CODE['削除']) {
        onChange({ target: { name: 'status', value: -1 } })
      }
    }
  }, [isDeleted, search, onChange])

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <form onSubmit={onSubmit}>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            backgroundColor: 'white',
            borderRadius: 1.5,
            mr: 5,
            maxWidth: 1024,
          }}
        >
          <IconButton
            ref={popperAnchorRef}
            onClick={() => setOpenPopper(!openPopper)}
            color="primary"
            style={{ color: '#4186FF' }}
            aria-label="Filter List"
            data-testid="search-filter"
          >
            <FilterListIcon color="action" />
          </IconButton>
          <DocNameSearch
            search={search}
            onSubmit={onSubmit}
            onChange={onChange}
            onFocusInOut={setOpenPopper}
          />
          <Popper
            open={openPopper}
            placement="bottom-start"
            anchorEl={popperAnchorRef.current}
            disablePortal
            ref={filterPopperRef}
            sx={{ width: 1 }}
            transition
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <Box>
                  <Filters
                    onCloseClick={() => setOpenPopper(false)}
                    onReset={handelResetClick}
                    onChange={onChange}
                    search={search as any}
                    usingCustomDate={usingCustomDate}
                    setUsingCustomDate={setUsingCustomDate}
                  />
                </Box>
              </Fade>
            )}
          </Popper>
        </Box>
      </form>
    </ClickAwayListener>
  )
}

export default SearchBar
